import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { User, UserFormData, LoginCredentials, RegisterData, ChangePasswordData } from '../types';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080', // Removed /api from here
//  baseURL: (process.env.REACT_APP_API_URL || 'http://localhost:8080') + '/api', // Add /api here
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor for API calls
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for API calls
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('API Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data
    });
    return response;
  },
  (error: AxiosError) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.config?.headers,
    });
    
    if (error.response?.status === 401) {
      console.log('Unauthorized access - redirecting to login');
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper function to handle API errors
const handleApiError = (error: any, context: string) => {
  console.error(`Error in ${context}:`, {
    status: error.response?.status,
    data: error.response?.data,
    config: {
      url: error.config?.url,
      method: error.config?.method,
      data: error.config?.data,
    },
  });
  throw error.response?.data?.message || `Failed to ${context}`;
};

// Auth API
export const authApi = {
  login: async (credentials: LoginCredentials): Promise<{ access_token: string; user: User }> => {
    try {
      const response = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'login');
    }
  },

  register: async (data: RegisterData): Promise<{ access_token: string; user: User }> => {
    try {
      const response = await api.post('/auth/register', data);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'register');
    }
  },

  refreshToken: async (): Promise<{ access_token: string }> => {
    try {
      const response = await api.post('/auth/refresh-token');
      return response.data;
    } catch (error) {
      return handleApiError(error, 'refresh token');
    }
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('access_token');
    } catch (error) {
      handleApiError(error, 'logout');
    }
  },
};

// Users API
export const usersApi = {
  // Get all users
  getAll: async (): Promise<User[]> => {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error) {
      return handleApiError(error, 'fetch users');
    }
  },

  // Get user by ID
  getById: async (id: number): Promise<User> => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError(error, `fetch user ${id}`);
    }
  },

  // Get current user profile
  getProfile: async (): Promise<User> => {
    try {
      const response = await api.get('/users/profile');
      return response.data;
    } catch (error) {
      return handleApiError(error, 'fetch profile');
    }
  },

  // Create new user
  create: async (userData: UserFormData): Promise<User> => {
    try {
      const response = await api.post('/users', userData);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'create user');
    }
  },

  // Create new client user (without password)
  createClient: async (userData: Omit<UserFormData, 'password' | 'confirmPassword'>): Promise<User> => {
    try {
      const response = await api.post('/users/client', userData);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'create client user');
    }
  },

  // Update user
  update: async (id: number, userData: Partial<UserFormData>): Promise<User> => {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      return handleApiError(error, `update user ${id}`);
    }
  },

  // Delete user
  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/users/${id}`);
    } catch (error) {
      handleApiError(error, `delete user ${id}`);
    }
  },

  // Update profile
  updateProfile: async (userData: Partial<UserFormData>): Promise<User> => {
    try {
      const response = await api.put('/users/profile', userData);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'update profile');
    }
  },

  // Change password
  changePassword: async (data: ChangePasswordData): Promise<void> => {
    try {
      await api.post('/users/change-password', data);
    } catch (error) {
      handleApiError(error, 'change password');
    }
  },

  // Reset password
  resetPassword: async (email: string): Promise<void> => {
    try {
      await api.post('/users/reset-password', { email });
    } catch (error) {
      handleApiError(error, 'reset password');
    }
  },
};

export default api;
